/** Number / currency / pct / date formatters used across the app.
 *
 * Centralised so we don't ad-hoc-roll formatters in every component.
 * All use `tabular-nums` via the body CSS, no padding hacks needed.
 */

export function formatUsd(
  value: number | null | undefined,
  opts: { compact?: boolean; sign?: boolean } = {},
): string {
  if (value == null || !Number.isFinite(value)) return '—'
  const { compact = false, sign = false } = opts
  const abs = Math.abs(value)
  const formatted =
    compact && abs >= 1000
      ? abs >= 1e6
        ? `${(value / 1e6).toFixed(2)}M`
        : `${(value / 1e3).toFixed(2)}k`
      : value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
  const signPrefix = sign && value > 0 ? '+' : ''
  return `${signPrefix}$${formatted}`
}

export function formatPct(
  value: number | null | undefined,
  opts: { digits?: number; sign?: boolean } = {},
): string {
  if (value == null || !Number.isFinite(value)) return '—'
  const { digits = 2, sign = false } = opts
  const signPrefix = sign && value > 0 ? '+' : ''
  return `${signPrefix}${value.toFixed(digits)}%`
}

export function formatNumber(
  value: number | null | undefined,
  digits = 0,
): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return value.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

/** Absolute date-time — "19 июн 14:32". RU locale, compact. */
export function formatDateTime(date: Date | string | null | undefined): string {
  if (date == null) return '—'
  const d = typeof date === 'string' ? new Date(date) : date
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('ru-RU', {
    day: '2-digit', month: 'short',
    hour: '2-digit', minute: '2-digit',
  })
}

/** Absolute date — "19 июн". RU locale. */
export function formatDate(date: Date | string | null | undefined): string {
  if (date == null) return '—'
  const d = typeof date === 'string' ? new Date(date) : date
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' })
}

/** Relative time — "2h ago", "just now", "3d ago".
 *  Accepts Date, ISO string, or null/undefined (returns "—"). */
export function timeAgo(date: Date | string | null | undefined): string {
  if (date == null) return '—'
  const d = typeof date === 'string' ? new Date(date) : date
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000)
  if (seconds < 30) return 'just now'
  if (seconds < 60) return `${seconds}s ago`
  const m = Math.floor(seconds / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const days = Math.floor(h / 24)
  if (days < 30) return `${days}d ago`
  return d.toLocaleDateString()
}
